import * as React from 'react';
import { Anchor } from 'telia-front-react/anchor';
import { Grid, GridColumn } from 'telia-front-react/grid';
import { Heading } from 'telia-front-react/heading';
import { Modal,IModalProps } from 'telia-front-react/modal';
import { Separator } from 'telia-front-react/separator';
import { Text } from 'telia-front-react/text';
import { scrollToElement } from 'telia-front-react/helper';
import {
  DisturbanceLocalNotifications,
  DisturbanceMapArea,
  DisturbanceMapCounty,
  DisturbanceMapDisturbance,
  DisturbanceRegionFull
} from '@config/DisturbanceConfig';
import DisturbanceNotice from '@disturbance/map/DisturbanceNotice';
import DisturbanceMapFailure from '@disturbance/map/DisturbanceMapFailure';
import { useStores } from "@stores/RootStore";
import TranslateStore from '@stores/TranslateStore';
import { getEnumValue } from '@util/KeyVal';

interface DisturbanceMapModalProps {
  /**
   * Modal properties
   */
  county: DisturbanceMapCounty;
  modal?: IModalProps;
  localNotices?: DisturbanceLocalNotifications;
}

const DisturbanceMapModal = (props: DisturbanceMapModalProps): React.ReactElement => {
  const { county, localNotices, modal } = props;
  const { ssoStore, disturbanceMapStore, translateStore } = useStores();

  React.useEffect(() => {
    setTimeout(() => {
      if (disturbanceMapStore.modalScrollId) {
        scrollToElement(document.getElementById(disturbanceMapStore.modalScrollId), 500000);
      }
    }, 0);
  });

  return county !== null && (
    <Modal {...modal} size="10-col" aria-labelledby="failures-modal-heading" aria-describedby="failures-description">
      <Text>
        <Heading className="h2" id="failures-modal-heading">
          {translateStore.translate(getEnumValue(DisturbanceRegionFull, county.countyKey)).toUpperCase()}
        </Heading>
        <p id="failures-description">
          {translateStore.translate('map.county.in')}
          <br />
          {!ssoStore.loggedIn && !ssoStore.isDealer && (
            <>
              {translateStore.translate('general.disturbance.login')}{' '}
              <Anchor
                onClick={(event) => {
                  event.preventDefault();
                  disturbanceMapStore.closeModal();
                  // workaround to avoid main-content inert bug
                  setTimeout(() => {
                    ssoStore.openSsoFrame();
                  }, 100);
                }}
                url="#"
              >
                {translateStore.translate('general.account.login')}
              </Anchor>
              .
            </>
          )}
        </p>
        {localNotices.noticeCount !== 0 ? getLocalNotices(localNotices, ssoStore.isDealer) : <></>}
        {county.affectedAreas.map((area, areaKey) => getAffectedArea(
          ssoStore.isDealer, area, areaKey, translateStore)
        )}
      </Text>
    </Modal>
  );
};

const getLocalNotices = (localNotices: DisturbanceLocalNotifications, isDealer: boolean): JSX.Element => {
  return (
    <Text>
      {localNotices.notices.map((notice, noticeKey) => (
        <DisturbanceNotice notice={notice} key={noticeKey} isDealer={isDealer} />
      ))}
      <Separator spacing="xsmall" />
    </Text>
  );
};

const getAffectedArea = (isDealer: boolean, area: DisturbanceMapArea, areaKey: number, translateStore?: TranslateStore): JSX.Element => {
  return (
    <Text key={areaKey}>
      <Grid align={['middle-xs']} key={areaKey}>
        <GridColumn width={['xs']}>
          <Heading element="h2" className="h4 text-bold">
            {area.areaName}
          </Heading>
        </GridColumn>
        <GridColumn width={['xs']}>
          {translateStore.translate('map.disturbance.count')}: <strong>{area.disturbancesCount}</strong>
        </GridColumn>
      </Grid>
      <Separator spacing="xsmall" />
      <Text>{area.disturbances.map((disturbance, disturbanceKey) => getDisturbance(isDealer, disturbance, areaKey, disturbanceKey))}</Text>
      <div className="padding" style={{ paddingTop: 25 }} />
    </Text>
  );
};

const getDisturbance = (isDealer: boolean, disturbance: DisturbanceMapDisturbance, areaKey: number, disturbanceKey: number): JSX.Element => {
  const refKey = `dist_map_id_${areaKey}${disturbanceKey}`;
  return <DisturbanceMapFailure key={disturbanceKey} refKey={refKey} isDealer={isDealer} disturbance={disturbance} />;
};

export default DisturbanceMapModal;
